import { youtubeId } from "@baze/schema";
import type { SectionProps } from "../context";
import { field, type RenderContext } from "../context";
import { Container, REVEAL, STACK, STAGGER, SectionHeader, SectionShell, cx } from "../ui";

type VideoItem = SectionProps<"videos">["node"]["content"]["items"][number];

const esc = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const thumb = (id: string) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

/**
 * A tiny page standing in for YouTube's player until it is played: the thumbnail and a play button,
 * no script. Playing navigates the frame to the real embed (autoplay), so a page of videos does not
 * load a player per video up front.
 */
function facade(id: string, label: string): string {
  return (
    "<style>*{margin:0}html,body{height:100%;overflow:hidden;background:#000}" +
    "a{position:absolute;inset:0;display:grid;place-items:center}" +
    "img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}" +
    "b{position:relative;width:68px;height:48px;border-radius:14px;background:rgba(15,15,15,.78);transition:background .2s}a:hover b,a:focus-visible b{background:#f00}" +
    'b:after{content:"";position:absolute;left:27px;top:14px;border-style:solid;border-width:10px 0 10px 17px;border-color:transparent transparent transparent #fff}</style>' +
    `<a href="https://www.youtube-nocookie.com/embed/${id}?autoplay=1" aria-label="${esc(`Putar video: ${label}`)}">` +
    `<img src="${thumb(id)}" alt="" loading="lazy"><b></b></a>`
  );
}

function Player({ ctx, item, index }: { ctx: RenderContext; item: VideoItem; index: number }) {
  const id = youtubeId(item.url);
  const label = item.title || `Video ${index + 1}`;
  const frame = "aspect-video overflow-hidden rounded-site-md bg-site-text/10 shadow-site-sm";
  if (!id) {
    // Editor only: the live site drops videos without a link.
    return (
      <div
        className={cx(
          frame,
          "grid place-items-center border border-dashed border-site-border p-6 text-center text-sm text-site-muted shadow-none",
        )}
      >
        Tempel tautan video YouTube.
      </div>
    );
  }
  if (ctx.editor || ctx.thumbnail) {
    // A still: clicks select the section, and no player loads in the editor or thumbnails.
    return (
      <div
        className={cx(frame, "relative grid place-items-center bg-black")}
        {...field(ctx, `content.items.${index}.url`)}
      >
        <img
          src={thumb(id)}
          alt=""
          loading="lazy"
          decoding="async"
          className="absolute inset-0 size-full object-cover"
        />
        <span aria-hidden="true" className="relative h-12 w-[68px] rounded-[14px] bg-[rgba(15,15,15,.78)]">
          <span className="absolute top-[14px] left-[27px] border-y-[10px] border-l-[17px] border-y-transparent border-l-white" />
        </span>
      </div>
    );
  }
  return (
    <div className={frame}>
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1`}
        srcDoc={facade(id, label)}
        title={`Video: ${label}`}
        loading="lazy"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
        className="h-full w-full border-0"
      />
    </div>
  );
}

function Card({
  ctx,
  item,
  index,
  large,
}: {
  ctx: RenderContext;
  item: VideoItem;
  index: number;
  large?: boolean;
}) {
  return (
    <figure className="min-w-0">
      <Player ctx={ctx} item={item} index={index} />
      {item.title || item.description ? (
        <figcaption className={large ? "mt-5" : "mt-3.5"}>
          {item.title ? (
            <h3
              className={cx(
                "baze-heading leading-snug text-site-text",
                large ? "text-xl @3xl:text-2xl" : "text-lg",
              )}
              {...field(ctx, `content.items.${index}.title`)}
            >
              {item.title}
            </h3>
          ) : null}
          {item.description ? (
            <p
              className={cx(
                "leading-relaxed text-site-muted",
                item.title && "mt-1.5",
                large ? "@3xl:text-lg" : "text-[0.9375rem]",
              )}
              {...field(ctx, `content.items.${index}.description`)}
            >
              {item.description}
            </p>
          ) : null}
        </figcaption>
      ) : null}
    </figure>
  );
}

const cols = (n: number) =>
  n === 1
    ? "mx-auto max-w-4xl"
    : n === 2 || n === 4
      ? "grid gap-x-6 gap-y-10 @2xl:grid-cols-2"
      : "grid gap-x-6 gap-y-10 @2xl:grid-cols-2 @4xl:grid-cols-3";

export function Videos({ node, ctx }: SectionProps<"videos">) {
  const c = node.content;
  // Keep each video's index in the definition for editor paths; the live site skips unlinked ones.
  const list = c.items
    .map((item, i) => ({ item, i }))
    .filter(({ item }) => ctx.editor || youtubeId(item.url));

  let body;
  if (list.length === 0) {
    body = ctx.editor ? (
      <p className="rounded-site-md border border-dashed border-site-border p-8 text-center text-sm text-site-muted">
        Tambahkan video YouTube.
      </p>
    ) : null;
  } else if (node.variant === "featured") {
    const [lead, ...rest] = list;
    body = (
      <>
        <div className={cx(REVEAL, "mx-auto max-w-4xl")}>
          <Card ctx={ctx} item={lead!.item} index={lead!.i} large />
        </div>
        {rest.length ? (
          <div
            className={cx(
              STAGGER,
              "mt-12 grid gap-x-6 gap-y-10 @2xl:grid-cols-2",
              rest.length > 2 && "@4xl:grid-cols-3",
            )}
          >
            {rest.map(({ item, i }) => (
              <Card key={i} ctx={ctx} item={item} index={i} />
            ))}
          </div>
        ) : null}
      </>
    );
  } else {
    body = (
      <div className={cx(STAGGER, cols(list.length))}>
        {list.map(({ item, i }) => (
          <Card key={i} ctx={ctx} item={item} index={i} large={list.length === 1} />
        ))}
      </div>
    );
  }

  if (!body) return null;
  return (
    <SectionShell ctx={ctx} node={node} labelledBy={!!c.title}>
      <Container>
        <SectionHeader
          ctx={ctx}
          node={node}
          eyebrow={c.eyebrow}
          title={c.title}
          description={c.description}
          align="center"
        />
        <div className={STACK}>{body}</div>
      </Container>
    </SectionShell>
  );
}
